import { Formik, Form } from 'formik'
import noop from 'lodash/noop'
import moment from 'moment'
import { map, pipe, pickAll, evolve, values as ramdaValues } from 'ramda'
import { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import * as Yup from 'yup'
import { Box, Checkbox, FieldMessage, Stack } from '@machobear/design-system'
import { Button } from '@/components/button'
import { confirm } from '@/components/dialog'
import {
  Row,
  Group,
  FieldInput,
  PhoneInput,
  FieldDropdown,
  FieldDatePicker
} from '@/components/form'
import useBasic from '@/hooks/use-basic'
import useMediaQuery from '@/hooks/use-media-query'
import { breakpoints } from '@/styles'
import { defaultIfFalsy } from '@/utils'
import '@/utils/validation'
import { CurrencyField } from '@/components/ds-form'
import { Text } from '@/components/onboarding-toaster/elements'

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 28px;
`

const RoommateForm = memo(({ data = {}, onCancel = noop, onSave = noop }) => {
  const dispatch = useDispatch()
  const { professions } = useBasic(['professions'])
  const { t } = useTranslation(['translation', 'basic'])
  const { relationships } = useBasic(['relationships'])
  const colSize = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`) ? 12 : 6

  const relationshipOptions = pipe(
    ramdaValues,
    map(a => ({
      value: a.txt_id,
      label: t(`basic:b.contact.relationships.${a.txt_id}.text`)
    }))
  )(relationships)

  const initialValues = pipe(
    pickAll([
      'id',
      'first_name',
      'last_name',
      'email',
      'phone_number',
      'dob',
      'relationship',
      'job_title',
      'prepay',
      'salary'
    ]),
    evolve({
      relationship: defaultIfFalsy(''),
      first_name: defaultIfFalsy(''),
      last_name: defaultIfFalsy(''),
      email: defaultIfFalsy(''),
      phone_number: defaultIfFalsy(''),
      dob: defaultIfFalsy(''),
      job_title: defaultIfFalsy(''),
      salary: defaultIfFalsy(''),
      prepay: defaultIfFalsy(false)
    })
  )(data)

  const OLD_INCOME_VALUES = [
    'No Income',
    '1-29k',
    '30k-59k',
    '60k-79k',
    '80k-99k',
    '100k-149k',
    '150k+'
  ]
  const getSalaryErrorMessage = salary => {
    if (salary.value === '' || !salary.value)
      return t(
        'r.profile.edit.livscore.income_update.error',
        'Required: Please enter an annual income or select the prepay checkbox.'
      )
  }
  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(),
    last_name: Yup.string().required(),
    email: Yup.string().email().trim().required(),
    phone_number: Yup.string().phone().required(),
    relationship: Yup.string().required(),
    dob: Yup.date(),
    job_title: Yup.string().required(),
    salary: Yup.number()
      .integer()
      .positive()
      .test(
        'old_value',
        t('r.profile.edit.livscore.cannot_be_range.error', 'Cannot be a range'),
        val => !OLD_INCOME_VALUES.includes(val)
      )
      .test(
        'prepay',
        (x, y) => getSalaryErrorMessage(x, y),
        (val, ctx) => {
          const { prepay } = ctx.parent
          return prepay || val
        }
      )
      .test(
        'len',
        t('r.profile.edit.livscore.income_length.error', 'Must be less than 10 digits'),
        (val, ctx) => ctx?.parent?.prepay || val?.toString().length <= 10
      )
  })

  const isEditing = !!data.id

  const handleDeleteClick = useCallback(() => {
    return confirm(
      t('r.profile.edit.delete_roommate.title', 'Delete Roommate'),
      t(
        'r.profile.edit.delete_roommate_confirmation.text',
        'Are you sure you want to delete this roommate?'
      )
    ).then(async () => {
      await dispatch.profile.deleteRoommate(data.id)
    })
  }, [data.id, dispatch.profile, t])

  const getProfessions = useCallback(() => {
    return pipe(
      ramdaValues,
      map(profession => ({
        value: profession.txt_id,
        label: t(`basic:b.professions.view.${profession.txt_id}.text`)
      }))
    )(professions)
  }, [professions, t])

  const onSubmit = async (values, { setSubmitting }) => {
    const action = isEditing ? dispatch.profile.updateRoommate : dispatch.profile.addRoommate
    const response = await action(values)
    setSubmitting(false)

    if (!response.error) {
      onSave()
    }
  }
  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ handleSubmit, values, isValid, isSubmitting, setFieldValue, errors, touched }) => (
        <Form>
          <Row>
            <Group col={colSize}>
              <FieldInput
                name="first_name"
                placeholder={t('r.profile.edit.first_name.label', 'First name')}
                required
              />
            </Group>
            <Group col={colSize}>
              <FieldInput
                name="last_name"
                placeholder={t('r.profile.edit.last_name.label', 'Last name')}
                required
              />
            </Group>
          </Row>
          <Row>
            <Group col={colSize}>
              <FieldInput
                name="email"
                placeholder={t('r.profile.edit.email.label', 'Email')}
                required
              />
            </Group>
            <Group col={colSize}>
              <PhoneInput
                name="phone_number"
                value={values.phone_number}
                countryCodeAlpha={data.phone_country_code_alpha}
                placeholder={t('r.profile.edit.phone_number.label', 'Phone number')}
                required
              />
            </Group>
          </Row>
          <Row>
            <Group col={colSize}>
              <FieldDatePicker
                name="dob"
                data-cy="dob"
                initialFocusedDate={moment().subtract(18, 'years')}
                placeholder={t('r.profile.edit.date_of_birth.label', 'Date of birth')}
                minDate={moment().subtract(100, 'years')}
                displayFormat="YYYY-MM-DD"
                disableFuture
              />
            </Group>
            <Group col={colSize}>
              <FieldDropdown
                id="relationship"
                name="relationship"
                placeholder={t('r.profile.edit.relationship.label', 'Relationship')}
                required
                options={relationshipOptions}
              />
            </Group>
          </Row>
          <Row>
            <Group col={colSize}>
              <Box width="full">
                <Stack space="small">
                  <CurrencyField
                    id="salary"
                    name="salary"
                    label={t('b.profile.general.annual_income.text', 'Annual Income')}
                    disabled={values?.prepay}
                  />
                  {errors?.salary && touched?.salary && (
                    <FieldMessage message={errors?.salary} tone="critical" />
                  )}
                </Stack>
                <Box marginTop="small" display="flex" alignItems="center" width="full">
                  <Checkbox
                    onChange={e => {
                      setFieldValue('salary', undefined)
                      setFieldValue('prepay', e.target.checked)
                    }}
                    disabled={values?.salary}
                    checked={values?.prepay}
                    tone={
                      !values.prepay && (values?.salary === '' || !values.salary)
                        ? 'critical'
                        : 'neutral'
                    }
                  ></Checkbox>
                  <Text>
                    {t('r.profile.edit.can_prepay_rent.text', 'I can prepay 12 months rent')}
                  </Text>
                </Box>
              </Box>
            </Group>
            <Group col={colSize}>
              <FieldDropdown
                id="job_title"
                name="job_title"
                placeholder={`${t('b.profile.general.job_title.text', 'Job Title')}`}
                options={getProfessions()}
                isSearchable
                required
              />
            </Group>
          </Row>
          <ButtonWrapper>
            {isEditing ? (
              <Button theme="delete" size="small" onClick={handleDeleteClick}>
                {t('common.delete', 'Delete')}
              </Button>
            ) : (
              <Button theme="secondary" size="small" onClick={onCancel}>
                {t('common.cancel', 'Cancel')}
              </Button>
            )}
            <Button
              id="close_form_button"
              theme="primary"
              size="small"
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !isValid}
            >
              {isEditing ? t('r.profile.edit.update.button', 'Update') : t('common.add', 'Add')}
            </Button>
          </ButtonWrapper>
        </Form>
      )}
    </Formik>
  )
})

export default RoommateForm
